import type { DashboardMode, FilterPresetDto, FilterPresetPayload } from '@nexus/contracts';
import { FilterPresetPayloadSchema } from '@nexus/contracts';
import { appliedStateToExploreSearch, type ExploreSearch } from '@/features/dashboard/lib/explore-search';
import { presetGlobalMatches } from '@/features/dashboard/lib/filter-preset-match';
import { useDashboardFiltersStore } from '@/features/dashboard/stores/dashboardFilters.store';
import { withSnapshotDates } from '@/features/dashboard/lib/explore-date-presets';
import type { CompetitorFilters, MyBrandFilters } from '@/features/dashboard/types/dashboard.types';
import {
  overviewAppliedKey,
  selectOverviewDraftKey,
  useOverviewFiltersStore,
} from '@/features/overview/stores/overviewFilters.store';

export function buildCurrentPresetPayload(mode: DashboardMode): FilterPresetPayload {
  const overview = useOverviewFiltersStore.getState();
  const dashboard = useDashboardFiltersStore.getState();
  const page = overview.applied;

  if (mode === 'my-brand') {
    return FilterPresetPayloadSchema.parse({
      mode: 'my-brand',
      page,
      global: dashboard.appliedMyBrandFilters,
    });
  }

  return FilterPresetPayloadSchema.parse({
    mode: 'competitors',
    page,
    global: dashboard.appliedCompetitorFilters,
  });
}

export function applyFilterPreset(preset: FilterPresetDto): ExploreSearch {
  const { filters } = preset;
  const dashboard = useDashboardFiltersStore.getState();

  useOverviewFiltersStore.getState().loadPresetState(filters.page);

  if (filters.mode === 'my-brand') {
    const myBrandFilters = withSnapshotDates(filters.global as MyBrandFilters);
    useDashboardFiltersStore.getState().hydrateFromApplied({
      mode: 'my-brand',
      competitorFilters: dashboard.appliedCompetitorFilters,
      myBrandFilters,
    });
    useDashboardFiltersStore.getState().applyFilters();
    return appliedStateToExploreSearch('my-brand', dashboard.appliedCompetitorFilters, myBrandFilters);
  }

  const competitorFilters = withSnapshotDates(filters.global as CompetitorFilters);
  useDashboardFiltersStore.getState().hydrateFromApplied({
    mode: 'competitors',
    competitorFilters,
    myBrandFilters: dashboard.appliedMyBrandFilters,
  });
  useDashboardFiltersStore.getState().applyFilters();
  return appliedStateToExploreSearch('competitors', competitorFilters, dashboard.appliedMyBrandFilters);
}

/** True when live draft filters match a preset's saved page + global payload (for post-refresh restore). */
export function presetMatchesCurrentFilters(preset: FilterPresetDto): boolean {
  const overview = useOverviewFiltersStore.getState();
  const dashboard = useDashboardFiltersStore.getState();
  const presetMode = preset.filters.mode;

  if (selectOverviewDraftKey(overview) !== overviewAppliedKey(preset.filters.page)) {
    return false;
  }

  return presetGlobalMatches(
    presetMode,
    dashboard,
    preset.filters.global as CompetitorFilters | MyBrandFilters,
  );
}
